import { useQuery } from "@tanstack/vue-query";
import { computed, type MaybeRefOrGetter, ref, toValue, watch } from "vue";
import { readLimitRefusal } from "@/shared/billing/limit-refusal";
import { HttpError, request } from "@/shared/http/client";
import type {
	Assignment,
	ThreadHit,
	ThreadMessage,
	ThreadStatus,
	ThreadSummary,
} from "./inbox.types";

/**
 * The same, for what a conversation looks like IN WORDS: the glyph of a channel,
 * the key that names it, the two letters of an avatar. Re-exported because the
 * components that draw them never call a hook.
 *
 * What is NOT here any more is every sentence — "agora", "Hoje", "Chat do site".
 * Those moved to `i18n/use-inbox-i18n.ts`, which is the only place that knows
 * which language this person reads in.
 */
export { channelIcon, channelKey, initials } from "./format";
/**
 * The shapes, next door.
 *
 * Re-exported so every component that imports a type from here keeps working: they were
 * never importing the queries, and this file passed three hundred lines describing what a
 * conversation IS before it got to how one is read.
 */
export type {
	Assignment,
	ThreadHit,
	ThreadMessage,
	ThreadStatus,
	ThreadSummary,
} from "./inbox.types";

/**
 * Everything the list can be narrowed by.
 *
 * One object rather than eight arguments, because the panel sets them together and the
 * counts have to be asked with exactly the same narrowing — a header saying "8 na fila"
 * over a list showing six is worse than no header.
 */
export interface InboxFilters {
	status: MaybeRefOrGetter<ThreadStatus | "all">;
	assignment: MaybeRefOrGetter<Assignment>;
	search: MaybeRefOrGetter<string>;
	/**
	 * `recent` — what happened last, which is what a list of conversations means.
	 * `waiting` — who has been waiting longest, for clearing a backlog.
	 */
	sort?: MaybeRefOrGetter<"recent" | "waiting">;
	/** -1 baixa … 2 urgente. Null is "any", which is not the same as 0. */
	priority?: MaybeRefOrGetter<number | null>;
	channelId?: MaybeRefOrGetter<string | null>;
	/** `yyyy-mm-dd`, as `<input type="date">` gives them. */
	from?: MaybeRefOrGetter<string | null>;
	to?: MaybeRefOrGetter<string | null>;
}

/** The filters as the server reads them, and as the query key remembers them. */
export function paramsOf(filters: InboxFilters): URLSearchParams {
	const query = new URLSearchParams();
	const status = toValue(filters.status);
	if (status !== "all") query.set("status", status);
	const assignment = toValue(filters.assignment);
	if (assignment !== "anyone") query.set("assignment", assignment);
	const search = String(toValue(filters.search)).trim();
	if (search) query.set("q", search);
	// Optional, because the home page reads the inbox for a count and has no opinion
	// about order — and defaulting there to the server's is exactly right.
	const sort = toValue(filters.sort);
	if (sort && sort !== "recent") query.set("sort", sort);

	const priority = toValue(filters.priority);
	// `!= null` and not a truthiness test: priority -1 and 0 are both real values, and
	// `if (priority)` drops exactly the two an operator uses to find the quiet ones.
	if (priority != null) query.set("priority", String(priority));
	const channelId = toValue(filters.channelId);
	if (channelId) query.set("channelId", channelId);

	const from = toValue(filters.from);
	// Whole days: a date field means "that day", and `2026-07-31` parsed bare is midnight
	// UTC — so "até 31/07" would cut the 31st off entirely.
	if (from) query.set("from", `${from}T00:00:00`);
	const to = toValue(filters.to);
	if (to) query.set("to", `${to}T23:59:59`);

	return query;
}

/** So the query key changes with the filters and with nothing else. */
function keyOf(filters: InboxFilters): string {
	return paramsOf(filters).toString();
}

/**
 * The inbox list, a page at a time.
 *
 * ── WHY A CURSOR AND NOT A PAGE NUMBER ────────────────────────────────────
 *
 * The list reorders itself whenever anybody writes, so "page 2" two ticks later shows rows
 * the reader has already seen and skips ones they never will. The server answers with
 * `nextCursor`, which is where THIS page stopped in the ordering it was asked for.
 *
 * ── WHY THE PAGES AFTER THE FIRST ARE HELD BY HAND ────────────────────────
 *
 * Because only the first is worth polling. A paginated query re-reads every page it holds
 * on every tick, so an operator who scrolled to page six would pull six pages fifteen
 * times a minute; and `maxPages: 1` — the knob that sounds like the fix — DISCARDS the
 * first page the moment the second arrives, which is infinite scroll that scrolls once.
 */
export function useThreads(filters: InboxFilters) {
	const rest = ref<ThreadSummary[]>([]);
	const loadingMore = ref(false);
	const exhausted = ref(false);

	const first = useQuery({
		queryKey: ["inbox", "list", () => keyOf(filters)] as const,
		queryFn: () => request<Page>(`/v3/conversations?${paramsOf(filters)}`),
		// SEM relógio (27/08, decisão do dono: nenhuma rota em polling permanente).
		// Quem refaz esta leitura é o ACONTECIMENTO: o stream de tenant
		// (`use-queue-alert`) invalida `["inbox"]` em `inbound`/`done`/`queued`, e o
		// regresso de uma conexão caída reconcilia o buraco. Cada 304 do relógio
		// custava a consulta inteira no servidor, em toda aba, o dia todo.
		refetchInterval: false,
	});

	// A different question is a different list. Keeping the pages scrolled under the old
	// filters would show rows the new ones exclude.
	watch(
		() => keyOf(filters),
		() => {
			rest.value = [];
			exhausted.value = false;
		},
	);

	/**
	 * Page one plus whatever has been scrolled to, with page one winning.
	 *
	 * The de-duplication is not defensive: a conversation somebody writes into MOVES to
	 * the top, and without this it would be drawn twice — once where it now is and once
	 * where it was when that page was fetched.
	 */
	const data = computed(() => {
		const head = first.data.value?.conversations ?? [];
		const seen = new Set(head.map((row) => row.id));
		return [...head, ...rest.value.filter((row) => !seen.has(row.id))];
	});

	const hasMore = computed(() => !exhausted.value && !!cursorAt());

	function cursorAt(): string | undefined {
		return rest.value.length > 0 ? lastCursor.value : first.data.value?.nextCursor;
	}
	const lastCursor = ref<string | undefined>();

	async function loadMore(): Promise<void> {
		const cursor = cursorAt();
		if (!cursor || loadingMore.value) return;
		loadingMore.value = true;
		try {
			const params = paramsOf(filters);
			params.set("cursor", cursor);
			const page = await request<Page>(`/v3/conversations?${params}`);
			rest.value = [...rest.value, ...page.conversations];
			lastCursor.value = page.nextCursor;
			if (!page.nextCursor) exhausted.value = true;
		} finally {
			loadingMore.value = false;
		}
	}

	watch(data, noteHeld);

	return { data, isLoading: first.isLoading, hasMore, loadingMore, loadMore };
}

/**
 * AS CONVERSAS RETIDAS pelo teto de contatos, como a LISTA as viu por último.
 *
 * Estado de módulo porque quem sabe é a lista e quem precisa é a conversa aberta
 * (`useThread`), que é outra consulta noutro painel: abrir uma linha que a lista já
 * disse estar retida não pede as mensagens — o servidor recusaria com
 * `CONTACT_LIMIT` de qualquer jeito. E é reativo de propósito: depois da compra a
 * lista é relida, o servidor solta a conversa, a linha chega com `held: false` e a
 * conversa aberta passa a ler as mensagens sozinha.
 */
const heldIds = ref<ReadonlySet<string>>(new Set());

/** Só as linhas vistas mudam: uma conversa fora deste filtro guarda o que se sabia dela. */
export function noteHeld(rows: readonly ThreadSummary[]): void {
	const next = new Set(heldIds.value);
	for (const row of rows) {
		if (row.held) next.add(row.id);
		else next.delete(row.id);
	}
	heldIds.value = next;
}

interface Page {
	conversations: ThreadSummary[];
	/** Absent when the page was not full — which is how the screen knows to stop asking. */
	nextCursor?: string;
}

/** The four counts, same narrowing, CALLER's cadence — `use-nav-counts.ts` says why. */
// Sem relógio por padrão: os contadores mudam pelos MESMOS eventos que mudam a
// lista, e a invalidação de `["inbox"]` no stream de tenant alcança esta chave.
export function useQueueStats(filters: InboxFilters, refetchInterval: number | false = false) {
	return useQuery({
		queryKey: ["inbox", "stats", () => keyOf(filters)] as const,
		queryFn: () =>
			request<{ all: number; mine: number; queued: number; unanswered: number }>(
				`/v3/conversations/queue/stats?${paramsOf(filters)}`,
			),
		refetchInterval,
	});
}

/** A page of a conversation. Thirty bubbles is more than a screen shows. */
const PAGE = 30;

/**
 * One conversation, read BACKWARDS.
 *
 * ── WHY THE OLDER PAGES ARE HELD HERE AND NOT IN THE QUERY ────────────────
 *
 * Because they must never be refetched. This screen polls every four seconds, and a
 * paginated query re-reads every page it holds on every tick — so an operator who
 * scrolled back through eight hundred messages would then pull all eight hundred, fifteen
 * times a minute, for as long as the conversation stayed open.
 *
 * So the QUERY owns the newest page — the one where new messages appear, which is the
 * only one worth polling — and the pages before it are fetched once, by hand, and kept.
 * They cannot change: a message already written does not move, and the one thing that
 * does change about an old message (its delivery tick) is only interesting on the newest.
 */
export function useThread(id: MaybeRefOrGetter<string | null>) {
	const older = ref<ThreadMessage[]>([]);
	const loadingPrevious = ref(false);
	const reachedStart = ref(false);

	const query = useQuery({
		queryKey: ["inbox", "thread", () => toValue(id)] as const,
		// `null` só existe no cache: é a conversa apagada (`useDeleteThread`).
		queryFn: () => request<ThreadPage | null>(`/v3/conversations/${toValue(id)}?limit=${PAGE}`),
		enabled: () => !!toValue(id) && !heldIds.value.has(toValue(id) ?? ""),
		// SEM relógio: a conversa aberta é a mais coberta por eventos — `inbound` e
		// `status` refazem a leitura na rajada (stream da conversa), `done` refaz no
		// fim do turno, e o stream de tenant cobre a queda pelo regresso.
		refetchInterval: false,
	});

	// A different conversation is a different thread of history. Keeping the previous
	// one's pages would splice two people's messages into one transcript.
	watch(
		() => toValue(id),
		() => {
			older.value = [];
			reachedStart.value = false;
		},
	);

	/**
	 * O buraco da janela deslizante, fechado.
	 *
	 * A query segura sempre as ÚLTIMAS 30. Numa conversa ativa, cada mensagem
	 * nova empurra a mais antiga para fora da janela — e ela não estava em lugar
	 * nenhum: `older` só guarda o que foi buscado ao rolar. Quem tinha subido o
	 * histórico ficava com um buraco silencioso entre `older` e a janela, sem
	 * nenhuma pista visual. O que desliza para fora é movido para `older`, onde
	 * as páginas guardadas já vivem — mesma conversa apenas, porque na troca de
	 * conversa o watch acima já zerou tudo.
	 */
	watch(
		() => ({ thread: toValue(id), page: query.data.value?.messages ?? null }),
		(now, was) => {
			if (!was || was.thread !== now.thread) return;
			if (!was.page?.length || !now.page?.length) return;
			const start = now.page[0];
			if (!start) return;
			const kept = new Set(older.value.map((m) => m.id));
			const slid = was.page.filter((m) => m.at < start.at && !kept.has(m.id));
			if (slid.length > 0) older.value = [...older.value, ...slid];
		},
	);

	// Por id, o mais novo vence: o rascunho aprovado ganha a hora do envio e volta
	// na página nova, enquanto a cópia velha pode estar em `older`.
	const loaded = computed(() => {
		const byId = new Map<string, ThreadMessage>();
		for (const m of [...older.value, ...(query.data.value?.messages ?? [])]) {
			byId.delete(m.id);
			byId.set(m.id, m);
		}
		return [...byId.values()];
	});

	/**
	 * O RASCUNHO PENDENTE MORA NO FIM (21/10) — onde vai sair quando aprovado. Ele
	 * guarda a hora em que foi escrito; o aprovado ganha a do envio e descia para
	 * baixo dos que ainda esperavam.
	 */
	const messages = computed(() => [
		...loaded.value.filter((m) => m.status !== "held"),
		...loaded.value.filter((m) => m.status === "held"),
	]);

	const hasPrevious = computed(() => {
		if (reachedStart.value) return false;
		return older.value.length > 0 || query.data.value?.hasMore === true;
	});

	/**
	 * The page before the oldest line on screen.
	 *
	 * `before` is that line's timestamp and the server answers strictly older than it, so
	 * two pages never overlap and nothing has to be de-duplicated.
	 */
	async function loadPrevious(): Promise<void> {
		const conversationId = toValue(id);
		const oldest = loaded.value[0];
		if (!conversationId || !oldest || loadingPrevious.value || !hasPrevious.value) return;

		loadingPrevious.value = true;
		try {
			const page = await request<ThreadPage>(
				`/v3/conversations/${conversationId}?limit=${PAGE}&before=${encodeURIComponent(oldest.at)}`,
			);
			older.value = [...page.messages, ...older.value];
			if (!page.hasMore) reachedStart.value = true;
		} finally {
			loadingPrevious.value = false;
		}
	}

	return {
		conversation: computed(() => query.data.value?.conversation ?? null),
		/**
		 * Retida pelo teto de contatos: pela lista (e então nem se pediu), ou pela recusa
		 * de quem abriu pelo endereço antes de a lista chegar.
		 */
		held: computed(() => {
			const threadId = toValue(id);
			if (!threadId) return false;
			return (
				heldIds.value.has(threadId) || readLimitRefusal(query.error.value)?.code === "CONTACT_LIMIT"
			);
		}),
		/** A conversa aberta deixou de existir: apagada aqui, ou um 404 (apagada noutro lugar). */
		gone: computed(
			() =>
				query.data.value === null ||
				(query.error.value instanceof HttpError && query.error.value.kind === "notFound"),
		),
		messages,
		isLoading: query.isLoading,
		hasPrevious,
		loadingPrevious,
		loadPrevious,
		refetch: query.refetch,
	};
}

interface ThreadPage {
	conversation: ThreadSummary;
	messages: ThreadMessage[];
	/** Whether anything exists before this page. A button that always shows is a lie. */
	hasMore: boolean;
}

/**
 * Finding words inside the open conversation.
 *
 * Answers positions — "3 de 12" — and not a filtered thread. Hiding everything that did
 * not match turns a conversation into a list of fragments, and the operator searching is
 * usually looking for what was said AROUND the words they remember.
 */
export function useThreadSearch(
	id: MaybeRefOrGetter<string | null>,
	query: MaybeRefOrGetter<string>,
) {
	return useQuery({
		queryKey: ["inbox", "thread-search", () => toValue(id), () => toValue(query)] as const,
		queryFn: async (): Promise<ThreadHit[]> => {
			const res = await request<{ hits: ThreadHit[] }>(
				`/v3/conversations/${toValue(id)}/messages/search?q=${encodeURIComponent(String(toValue(query)).trim())}`,
			);
			return res.hits;
		},
		// Two characters is where a search inside one conversation stops being every line
		// of it. Below that the answer is noise and the request is waste.
		enabled: () => !!toValue(id) && String(toValue(query)).trim().length >= 2,
	});
}
